import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatTanggal } from "@/lib/api";
import { Bell, Check, CheckCheck, Megaphone, AlertCircle, BookOpen, MessageSquare, ShoppingBag, CalendarCheck, FileText, Users, Award, Banknote, Video } from "lucide-react";

const ICONS = {
  pemberitahuan: Megaphone,
  kendala: AlertCircle,
  buku_penghubung: BookOpen,
  saran_kritik: MessageSquare,
  peminjaman: ShoppingBag,
  jadwal: CalendarCheck,
  cp: FileText,
  pendampingan: Users,
  ekskul_progsus: Award,
  gaji: Banknote,
  lms: Video,
};

const ICON_COLORS = {
  pemberitahuan: "bg-indigo-50 text-indigo-600",
  kendala: "bg-rose-50 text-rose-600",
  buku_penghubung: "bg-amber-50 text-amber-600",
  saran_kritik: "bg-sky-50 text-sky-600",
  peminjaman: "bg-emerald-50 text-emerald-600",
  jadwal: "bg-violet-50 text-violet-600",
  cp: "bg-cyan-50 text-cyan-600",
  pendampingan: "bg-teal-50 text-teal-600",
  ekskul_progsus: "bg-orange-50 text-orange-600",
  gaji: "bg-lime-50 text-lime-600",
  lms: "bg-fuchsia-50 text-fuchsia-600",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 15 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) { console.error("Load notifications failed:", e); }
  }, []);

  useEffect(() => {
    load();
    // Poll setiap 15 detik untuk pengalaman real-time
    const id = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); }
    catch (e) { console.error(e); }
  };
  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); }
    catch (e) { console.error(e); }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((s) => !s)} className="relative w-10 h-10 rounded-lg hover:bg-slate-100 text-slate-700 flex items-center justify-center" data-testid="bell-btn" aria-label="Notifikasi">
        <Bell className="w-5 h-5" strokeWidth={1.75} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="bell-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-fade-up" data-testid="bell-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h4 className="font-semibold text-slate-900">Notifikasi</h4>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1" data-testid="mark-all-read">
                <CheckCheck className="w-3.5 h-3.5" /> Tandai semua dibaca
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 && (
              <div className="p-10 text-center text-slate-400 italic text-sm" data-testid="bell-empty">Belum ada notifikasi.</div>
            )}
            {items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              const colorClass = ICON_COLORS[n.type] || "bg-slate-50 text-slate-600";
              const content = (
                <div className={`flex items-start gap-3 px-4 py-3 border-b border-slate-100 transition-colors ${n.read ? "opacity-60 hover:bg-slate-50" : "bg-indigo-50/30 hover:bg-indigo-50"}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900 leading-snug">{n.title}</p>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-indigo-500 mt-1.5 shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{formatTanggal(n.created_at)}</p>
                  </div>
                  {!n.read && (
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); markRead(n.id); }} className="text-slate-400 hover:text-indigo-600 shrink-0" title="Tandai dibaca">
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
              return n.link ? (
                <Link key={n.id} to={n.link} onClick={() => { setOpen(false); if (!n.read) markRead(n.id); }} data-testid={`notif-${n.id}`}>
                  {content}
                </Link>
              ) : (
                <div key={n.id} data-testid={`notif-${n.id}`}>{content}</div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
